import {Injectable} from '@angular/core';
import {Chart} from 'highcharts';
import {LineChartSettingsParameters} from '@models/charts/line-chart-settings-parameters';
import {Signal} from '@models/dashboard/signal';
import {ChartSignalEntry} from '@models/pages/user-defined-page/chart-signal-entry';
import {ChartsConfigurationHelperService} from '@services/charts/helper/charts-configuration-helper.service';
import {ChartConstants} from '@constants/pages/chart-constants';

@Injectable({
  providedIn: 'root'
})
export class BarChartDataUpdateService {

  constructor(private chartsHelperService: ChartsConfigurationHelperService) {
  }

  public updateChartData(chart: Chart, chartSettings: LineChartSettingsParameters, valuesArray) {

    const xAxis = chart.xAxis;
    const series = chart.series;

    if (!this.isDataUpdatePossible(xAxis, series, valuesArray)) {
      return;
    }

    const value = valuesArray[0];
    for (const item of value.vs) {
      const signalId = item.s;
      let signalDefinition: ChartSignalEntry | undefined = undefined;
      if (chartSettings.isTemplateWidget) {
        signalDefinition = this.getSignalDefinitionByName(signalId, chartSettings);
      } else {
        signalDefinition = this.getSignalDefinitionById(signalId, chartSettings);
      }
      if (!signalDefinition) {
        continue;
      }
      const signalSeries = series.find(item => item.options.id === String(signalDefinition.signalId));
      const point = signalSeries?.data[0];
      if (point) {
        point.color = this.getColorByValue(item.v, signalDefinition);
        point.options['ts'] = value.ts;
        point.update(parseFloat(item.v));
      }
    }

  }

  private isDataUpdatePossible(xAxis, series, valuesArray) {
    if (!xAxis || xAxis.length === 0 ||
        !series || series.length === 0) {
      return false;
    }
    if (!valuesArray || valuesArray.length === 0 || !valuesArray[0] || !valuesArray[0].vs || valuesArray[0].vs.length === 0) {
      return false;
    }
    return true;
  }


  private getColorByValue(value: number, signalDefinition: ChartSignalEntry): string {

    if (!signalDefinition) {
      return ChartConstants.DEFAULT_LINE_COLOR;
    }

    let color: number;

    if (value < signalDefinition.boundaries.area1End) {
      color = signalDefinition.colors.area1;
    } else if (value >= signalDefinition.boundaries.area1End && value < signalDefinition.boundaries.area2End) {
      color = signalDefinition.colors.area2;
    } else {
      color = signalDefinition.colors.area3;
    }

    return this.chartsHelperService.convertNumberToHexColor(color);
  }

  private getSignalDefinitionById(signalId, parameters: LineChartSettingsParameters): ChartSignalEntry | undefined {
    const chartSignals: ChartSignalEntry[] = parameters.chartSettings.signals;
    for (const chartSignal of chartSignals) {
      const machineSignal = Object.values(parameters.machineSignals).find((item: Signal) => {
        return item.id === chartSignal.signalId;
      });
      if (machineSignal && machineSignal.id === Number(signalId)) {
        chartSignal.unit = machineSignal.unit;
        chartSignal.precision = machineSignal.decimals;
        chartSignal.displayedText = machineSignal.label;
        return chartSignal;
      } else if (chartSignal.signalId === signalId) {
        return chartSignal;
      }

    }
    return undefined;
  }

  private getSignalDefinitionByName(signalId: number, parameters: LineChartSettingsParameters): ChartSignalEntry | undefined {
    const chartSignals: ChartSignalEntry[] = parameters.chartSettings.signals;
    for (const chartSignal of chartSignals) {
      if (chartSignal.signalId != signalId) {
        continue;
      }
      const machineSignal = Object.values(parameters.machineSignals).find((item: Signal) => {
        return item.name.toLowerCase() === chartSignal.signalName.toLowerCase();
      });
      if (machineSignal) {
        chartSignal.unit = machineSignal.unit;
        chartSignal.precision = machineSignal.decimals;
        chartSignal.displayedText = machineSignal.label;
        return chartSignal;
      }

    }
    return undefined;
  }
}
